import { css } from 'lit';

const visuallyHiddenCss = css`
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  border: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  white-space: nowrap;
`;

export const styles = css`
  :host {
    --nav-bar-width: 260px;

    display: block;
    height: 100vh;
  }

  :host:has(.sidebar-closed) {
    --nav-bar-width: fit-content;
  }

  * {
    box-sizing: border-box;
  }

  nav {
    --menu-font-size: var(--font-size-200);
    max-width: var(--nav-bar-width);
    width: var(--nav-bar-width);
    min-height: 100vh;
    height: max-content;
    display: flex;
    align-items: start;
    gap: var(--space-scale-3-5);
    flex-direction: column;
    background-color: var(--color-surface-inverted-100);
    color: var(--color-text-inverted-400);
    padding-top: var(--space-scale-2);
    padding-bottom: var(--space-scale-2);
    z-index: 1;

    .nav-items {
      display: flex;
      flex-direction: column;
      padding: 0 var(--space-scale-1);
      width: 100%;
    }

    .nav-items.sidebar-closed {
      a span {
        ${visuallyHiddenCss}
      }

      details summary span {
        ${visuallyHiddenCss}
      }
    }

    .sidebar-header {
      width: 100%;
      display: grid;
      grid-template-columns: 1fr auto;
      align-items: center;
      justify-content: center;
      padding: 0 var(--space-scale-2);

      button {
        background-color: transparent;
        color: var(--color-text-inverted-400);
        border: none;
      }
    }

    .sidebar-header.sidebar-closed {
      padding: 0 var(--space-scale-1);
      .logo {
        display: none;
        visibility: hidden;
      }
    }

    hr {
      width: 100%;
      height: 1px;
      background-color: var(--color-surface-inverted-200);
      border: none;
    }

    .tertiary-nav {
      display: flex;
      flex-direction: column;
      margin-top: auto;
      width: 100%;

      .external-link {
        font-size: var(--menu-font-size);
        line-height: var(--font-line-height-100);

        .icon {
          width: var(--space-scale-2);
          height: var(--space-scale-2);
        }
      }
    }

    .tertiary-nav.sidebar-closed {
      .external-link,
      hr,
      #client-selector {
        display: none;
        visibility: hidden;
      }

      a span {
        ${visuallyHiddenCss}
      }
    }

    .icon {
      display: flex;
      align-items: center;
      width: var(--space-scale-3);
      height: var(--space-scale-3);
      color: var(--nav-item-color);
    }

    .nav-item {
      display: flex;
      font-size: var(--menu-font-size);
      line-height: var(--font-line-height-100);
      gap: var(--space-scale-1-5);
      border-radius: var(--border-radius-100);
      color: var(--nav-item-color);
      align-items: center;
      padding: var(--space-scale-1);
      width: 100%;
      text-decoration: none;
    }
  }

  nav.sidebar-closed {
    width: fit-content;

    :host {
      --nav-bar-width: fit-content;
    }
  }

  details {
    border-radius: var(--border-radius-100);

    summary {
      font-size: var(--menu-font-size);
      line-height: var(--font-line-height-100);
      position: relative;
      display: flex;
      align-items: baseline;

      &::marker {
        display: none;
      }

      &::-webkit-details-marker {
        display: none;
      }
    }

    .chevron {
      margin-left: auto;
      margin-right: var(--space-scale-1);
      width: var(--space-scale-2);
    }

    &[open] .chevron svg {
      transform: rotate(180deg);
    }

    .dropdown-items {
      display: flex;
      flex-direction: column;
    }
  }

  details > summary {
    display: flex;
    gap: var(--space-scale-1-5);
    padding: var(--space-scale-1);
    border-radius: var(--border-radius-100);
    color: var(--nav-item-color);
    align-items: center;

    a {
      color: var(--nav-item-color);
    }

    &:hover,
    &:focus-visible {
      background-color: var(--color-surface-inverted-200);
      border-radius: var(--border-radius-100);
      text-decoration: none;
    }
  }

  .sidebar-closed details:has(.current-page) > summary {
    --nav-item-color: var(--color-primary-400);
    border-radius: var(--border-radius-100);
    background-color: var(--color-primary-100);
    color: var(--nav-item-color);
  }

  .sub-nav-item {
    width: 100%;
    display: flex;
    padding-left: 44px;
    padding-top: 10px;
    padding-bottom: 10px;
    color: var(--color-text-inverted-400);
    font-size: var(--menu-font-size);
    line-height: var(--font-line-height-100);
    text-decoration: none;

    button {
      color: var(--color-text-inverted-400);
      padding: 0;
    }
  }

  .sub-nav-item:hover,
  .sub-nav-item:focus-visible,
  .nav-item:hover,
  .nav-item:focus-visible {
    background-color: var(--color-surface-inverted-200);
    border-radius: var(--border-radius-100);
    text-decoration: none;
    color: var(--color-text-inverted-400);

    .icon {
      color: var(--color-text-inverted-400);
    }
  }

  .current-page {
    --nav-item-color: var(--color-primary-400);
    border-radius: var(--border-radius-100);
    background-color: var(--color-primary-100);
    color: var(--nav-item-color);
  }

  .link-page {
    display: flex;
    position: absolute;
    overflow: auto;
    flex-direction: column;
    gap: var(--space-scale-4);
    padding: var(--space-scale-5);
    left: var(--nav-bar-width);
    background-color: var(--color-surface-200);
    width: calc(100% - var(--nav-bar-width));
    height: 100%;
  }

  .tooltip {
    position: absolute;
    display: flex;
    white-space: nowrap;
    align-items: center;
    gap: var(--space-scale-1);
    color: black;
    background-color: var(--color-surface-600);
    padding: var(--space-scale-1);
    z-index: 1000;
    border-radius: var(--border-radius-50);
    font-weight: var(--font-weight-medium);
  }

  .nav-toggle-button {
    position: relative;
  }

  .nav-toggle-tooltip {
    left: 100%;
    top: 100%;
    visibility: hidden;
    opacity: 0;
  }

  .nav-toggle-button:hover .nav-toggle-tooltip {
    visibility: visible;
    opacity: 1;
  }

  #client-selector {
    display: flex;
    align-items: center;
    padding: var(--space-scale-1) 0;
    margin-bottom: var(--space-scale-2);
  }

  #client-selector tt-combobox {
    --tt-combobox-color: var(--color-text-inverted-400);
    --tt-combobox-dropdown-color: var(--color-text-inverted-400);
    --tt-combobox-list-background-color: var(--color-surface-inverted-100);
    --tt-combobox-option-background-color-hover: var(--color-surface-inverted-200);
    --tt-combobox-placeholder-color: var(--color-text-inverted-300);
    --tt-combobox-background-color: var(--color-surface-inverted-100);
    --tt-combobox-hover-background-color: var(--color-surface-inverted-200);
    --tt-combobox-list-max-width: 80ch;
    --tt-combobox-min-width: 244px;
    --tt-combobox-max-width: 244px;
    --tt-combobox-option-selected-color: var(--color-text-inverted-500);
    --tt-combobox-border-color: var(--color-surface-inverted-200);
  }

  .single-client-name {
    height: 39px;
    padding: var(--space-scale-1); /* match tt-combobox height */
    font-size: var(--font-size-200);
    color: var(--color-text-inverted-200);
    text-wrap: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;
